import { formatEther } from 'viem';

import {
  PunksmarketIndexerBid,
  PunksmarketIndexerCriteria,
  StashBidLike,
  UnifiedBidStatus,
  UnifiedMarketBid,
} from '@/models/unified-market-bid';
import {
  formatPunksmarketBidCriteria,
  formatPunksmarketCriteriaDisplay,
  classifyPunksmarketIndexerBid,
} from '@/utils/punksmarket-criteria.util';
import {
  nativeBidMarketplaceSource,
  nativeBidTypeLabel,
} from '@/utils/native-market-bid-labels';

function weiStringToEth(wei: string | bigint | number): number {
  try {
    return Number(formatEther(BigInt(wei)));
  } catch {
    return 0;
  }
}

export function summarizePunksmarketCriteria(
  criteria: PunksmarketIndexerCriteria | undefined,
  includeIds: number[],
  excludeIds: number[],
): string {
  return formatPunksmarketCriteriaDisplay(criteria, includeIds, excludeIds).summary;
}

export function mapStashBidToUnified(bid: StashBidLike): UnifiedMarketBid {
  const status = (bid.status ?? 'active') as UnifiedBidStatus;
  return {
    platform: 'stash',
    id: bid.id,
    bidderAddress: bid.bidder_address,
    amountEth: Number(bid.bid_amount_eth) || weiStringToEth(bid.bid_amount_wei),
    amountWei: bid.bid_amount_wei,
    createdAt: bid.created_at,
    status,
    bidTypeLabel: bid.bidTypeLabel ?? bid.bidType ?? 'Stash bid',
    criteriaSummary: bid.biddingOn ?? bid.decodedTags?.join(', '),
    punkIds: bid.punkIds,
    stashAddress: bid.stashAddress,
    confidential: bid.confidential,
    stashFunded: bid.stash_funded,
    bidType: bid.bidType,
    source: bid.source ?? 'stash',
    raw: bid,
  };
}

export function mapPunksmarketIndexerBidToUnified(
  item: PunksmarketIndexerBid,
): UnifiedMarketBid {
  const amountEth = weiStringToEth(item.bidWei);
  const settlementEth = weiStringToEth(item.settlementWei);
  const status: UnifiedBidStatus = item.active ? 'active' : 'cancelled';
  const { bidType, bidTypeLabel, display: criteriaDisplay } =
    classifyPunksmarketIndexerBid(item);
  const includeIds = item.includeIds ?? [];
  return {
    platform: 'punksmarket',
    id: `pm-${item.bidId}`,
    punksmarketBidId: item.bidId,
    bidderAddress: item.bidder,
    amountEth,
    amountWei: item.bidWei,
    createdAt: item.timestamp
      ? new Date(Number(item.timestamp) * 1000).toISOString()
      : undefined,
    status,
    bidType,
    bidTypeLabel,
    criteriaSummary: criteriaDisplay.summary,
    punkIds: includeIds.length ? [...includeIds] : undefined,
    settlementEth,
    source: 'punksmarket',
    raw: item,
  };
}

export function mapLegacyPunkBidToUnified(
  punkId: string,
  legacyBid: {
    value: string;
    fromAccount?: { id: string };
    blockTimestamp?: string;
    transactionHash?: string;
  },
): UnifiedMarketBid {
  const amountEth = weiStringToEth(legacyBid.value);
  return {
    platform: 'legacy',
    id: `legacy-${punkId}`,
    bidderAddress: legacyBid.fromAccount?.id ?? '',
    amountEth,
    amountWei: legacyBid.value,
    createdAt: legacyBid.blockTimestamp,
    status: 'active',
    bidTypeLabel: nativeBidTypeLabel,
    criteriaSummary: `Punk #${punkId}`,
    isLegacyBid: true,
    legacyBid,
    source: nativeBidMarketplaceSource,
    raw: legacyBid,
  };
}

/** Sort by amount descending; stable enough for UI lists. */
export function sortUnifiedBidsByAmount(
  bids: UnifiedMarketBid[],
): UnifiedMarketBid[] {
  return [...bids].sort((a, b) => b.amountEth - a.amountEth);
}

export function unifiedBidToStashTableShape(
  bid: UnifiedMarketBid,
): StashBidLike & {
  isLegacyBid?: boolean;
  legacyBid?: unknown;
  punksmarketIndexerBid?: PunksmarketIndexerBid;
} {
  if (bid.platform === 'stash' && bid.raw) {
    return bid.raw as StashBidLike;
  }
  const pmRaw =
    bid.platform === 'punksmarket' && bid.raw
      ? (bid.raw as PunksmarketIndexerBid)
      : undefined;
  const pmCriteria = pmRaw ? formatPunksmarketBidCriteria(pmRaw) : null;
  return {
    id: bid.id,
    bidder_address: bid.bidderAddress,
    bid_amount_wei: bid.amountWei,
    bid_amount_eth: bid.amountEth,
    created_at: bid.createdAt ?? '',
    status: bid.status,
    bidType: bid.bidType ?? (bid.platform === 'legacy' ? 'legacy' : bid.platform),
    bidTypeLabel: bid.bidTypeLabel,
    stashAddress: bid.stashAddress,
    confidential: bid.confidential,
    stash_funded: bid.stashFunded ?? 1,
    punkIds: bid.punkIds,
    punkIndices: bid.punkIds,
    decodedTags: pmCriteria?.tags,
    biddingOn: bid.criteriaSummary ?? pmCriteria?.summary,
    source: bid.source,
    platform: bid.platform,
    isLegacyBid: bid.isLegacyBid,
    legacyBid: bid.legacyBid,
    punksmarketBidId: bid.punksmarketBidId,
    punksmarketIndexerBid: pmRaw,
  };
}
